import type { CSSObject } from "@mui/material/styles";

/**
 * A marketplace result card lays itself out against its own width, not against the viewport.
 *
 * The same card is drawn in three very different places: the marketplace's results pane, which
 * shares the window with the navigation drawer and the filter column; the vendor-team picker, which
 * sits in a dialog or a proposal's side panel; and a phone. Before #5409 each card asked
 * `useMediaQuery` about the window and sized its avatar in `vw`, so a card in a 440px picker on a
 * wide monitor drew its desktop layout and a 15vw avatar.
 *
 * So the card names itself as a container and its children measure against that. A container cannot
 * query itself, which is why every rule below is written for an element *inside* the card.
 */
export const MARKETPLACE_RESULT_CONTAINER = "marketplace-result";

/** Applied to the card's root, so the queries below have something to resolve against. */
export const marketplaceResultContainment: CSSObject = {
  containerName: MARKETPLACE_RESULT_CONTAINER,
  containerType: "inline-size",
};

/**
 * Below this the card is a phone card: the avatar joins the header row, the description and the
 * facts take the full width, and the facts stack one to a row. Material's `sm`, the width the rest of
 * the application treats as a phone.
 */
export const MARKETPLACE_RESULT_PHONE_WIDTH = 600;

/**
 * Below this three columns of facts leave each one too little room to show a value, so the grid
 * drops to two. The mockups show three at 1040px and one at 390px; a 600-840px pane — the results
 * pane beside an open filter column — sits between them.
 */
export const MARKETPLACE_RESULT_TWO_COLUMN_WIDTH = 840;

export function marketplaceResultNarrowerThan(width: number): string {
  // Container query ranges are inclusive at both ends, so a rule at exactly `width` would otherwise
  // match both the narrow and the wide side, and source order would pick the winner.
  return `@container ${MARKETPLACE_RESULT_CONTAINER} (max-width: ${width - 0.05}px)`;
}

export function marketplaceResultIsPhone(): string {
  return marketplaceResultNarrowerThan(MARKETPLACE_RESULT_PHONE_WIDTH);
}

/** The avatar's side in pixels. Fixed per layout, so a long name never squeezes it. */
export const MARKETPLACE_RESULT_AVATAR_SIZE = {
  compact: 48,
  full: 88,
  phone: 56,
} as const;
